// PeriodManagement.jsx - Main Component for KPI Period Management

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { 
    Add as AddIcon, 
    Search as SearchIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import PeriodTable from './PeriodTable';
import PeriodDialog from './PeriodDialog';
import {
  fetchPeriodsRequest,
  setPeriodFilters,
  setPeriodPagination,
} from '../redux.actions';
import {
  selectPeriods,
  selectLoading,
  selectFilters,
  selectPagination,
} from '../redux.selectors';

const PeriodManagement = () => {
  const dispatch = useDispatch();
  const periods = useSelector(selectPeriods);
  const loading = useSelector(selectLoading);
  const filters = useSelector(selectFilters);
  const pagination = useSelector(selectPagination);
  const navigate = useNavigate();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedPeriod, setSelectedPeriod] = React.useState(null);
  // Local state for search input (immediate UI feedback)
  const [searchInput, setSearchInput] = useState('');
  const debounceTimerRef = useRef(null);

  // Initialize from URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search') || '';
    const offsetParam = params.get('page') ? parseInt(params.get('page'), 10) : 1;
    const limitParam = params.get('limit') ? parseInt(params.get('limit'), 10) : pagination.limit;
    // Update Redux state with URL params
    if (searchParam !== filters.search) {
      dispatch(setPeriodFilters({ search: searchParam }));
    }
    if (offsetParam !== pagination.offset || limitParam !== pagination.limit) {
      dispatch(setPeriodPagination({ offset: offsetParam, limit: limitParam }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local search input with Redux filter
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Sync URL with current state
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (pagination.offset && pagination.offset !== 1) params.set('page', pagination.offset.toString());
    if (pagination.limit && pagination.limit !== 10) params.set('limit', pagination.limit.toString());
    const newSearch = params.toString();
    if (location.search !== `?${newSearch}` && newSearch !== location.search.substring(1)) {
      navigate(`?${newSearch}`, { replace: true });
    }
  }, [filters.search, pagination.offset, pagination.limit, navigate, location.search]);

  // Fetch periods on mount and when filters/pagination change
  useEffect(() => {
    dispatch(
      fetchPeriodsRequest({
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset,
      })
    );
  }, [dispatch, filters, pagination.limit, pagination.offset]);

  // Debounced search handler
  const handleSearchChange = (event) => {
    const newSearch = event.target.value;
    setSearchInput(newSearch);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      if (newSearch === '' || newSearch.length >= 2) {
        dispatch(setPeriodFilters({ search: newSearch }));
        dispatch(setPeriodPagination({ offset: 1 }));
      }
    }, 500);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    dispatch(setPeriodFilters({ search: '' }));
    dispatch(setPeriodPagination({ offset: 1 }));
  };

  const handleAddPeriod = () => {
    setSelectedPeriod(null);
    setDialogOpen(true);
  };

  const handleEditPeriod = (period) => {
    setSelectedPeriod(period);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPeriod(null);
  };

  const handlePageChange = (newPage) => {
    dispatch(setPeriodPagination({ offset: newPage + 1 }));
  };

  const handleRowsPerPageChange = (newLimit) => {
    dispatch(setPeriodPagination({ limit: newLimit, offset: 1 }));
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Time Periods
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddPeriod}
        >
          Add Period
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={(theme) => ({ mb: 3, p: 2, boxShadow: 'none', border: `1px solid ${theme.palette.divider}` })}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search Filter */}
          <TextField
            placeholder="Search periods..."
            value={searchInput}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 250, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            helperText={searchInput.length > 0 && searchInput.length < 2 ? "Type at least 2 characters to search" : ""}
          />

          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<RefreshIcon />}
            disabled={!filters.search}
            size="small"
            sx={{ height: '36px' }}
          >
            Reset
          </Button>
        </Box>

        {/* Results count */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total Periods: <strong>{pagination.totalItems || 0}</strong>
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={(theme) => ({ width: '100%', overflow: 'hidden', boxShadow: 'none', border: `1px solid ${theme.palette.divider}` })}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && periods.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No periods found. Create your first period to get started.
            </Typography>
          </Box>
        )}

        {!loading && periods.length > 0 && (
          <>
            <PeriodTable
              periods={periods}
              loading={loading}
              pagination={pagination}
              onEdit={handleEditPeriod}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </>
        )}
      </Paper>

      <PeriodDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        selectedPeriod={selectedPeriod}
      />
    </Box>
  );
};

export default PeriodManagement;
